'use client';

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  BookOpen, Terminal, QrCode, Share2, BarChart3, 
  Search, Menu, X, ChevronRight, ArrowLeft 
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'

const docNavigation = [
  { group: 'Getting Started', items: [
    { label: 'Documentation Home', href: '/docs', icon: BookOpen },
  ]},
  { group: 'Core Products', items: [
    { label: 'QR Code Engine', href: '/docs/qr-code', icon: QrCode },
    { label: 'Referral Software', href: '/docs/referral', icon: Share2 },
    { label: 'Analytics Tracker', href: '/docs/analytics', icon: BarChart3 },
    { label: 'Headless Forms', href: '/docs/headless-forms', icon: Terminal },
  ]},
  { group: 'Developer References', items: [
    { label: 'API Reference', href: '/docs/api', icon: Terminal },
  ]}
]

export default function DocsLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Filter navigation items based on search query
  const filteredNav = docNavigation.map(group => {
    const matchedItems = group.items.filter(item => 
      item.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
    return { ...group, items: matchedItems }
  }).filter(group => group.items.length > 0)

  return (
    <div className="relative min-h-screen bg-black text-white flex flex-col font-sans selection:bg-primary/30">
      {/* Background Gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,102,255,0.05),transparent_50%)] pointer-events-none" />
      <div className="absolute top-[20%] left-[-10%] w-[50vw] h-[50vw] bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.03),transparent_70%)] blur-[120px] pointer-events-none" />

      {/* Global Navbar */}
      <MarketingNavbar />

      {/* Main Wrapper */}
      <div className="flex-1 max-w-[1440px] w-full mx-auto px-6 lg:px-12 pt-32 pb-24 flex flex-col lg:flex-row gap-8 relative z-10">
        
        {/* Mobile Sidebar Toggle Header */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <span className="font-bold text-sm tracking-tight">Lenklyst Documentation</span>
          </div>
          <button
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
          >
            {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Sidebar Container */}
        <aside className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-32 space-y-6">
            
            {/* Back to Home Button */}
            <Link href="/" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40 hover:text-white transition-colors group">
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
              Back to Lenklyst
            </Link>

            {/* Title */}
            <div>
              <div className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                DOCS <span className="text-primary font-mono text-sm">v1.0</span>
              </div>
              <p className="text-xs text-white/40 mt-1">Unified branding &amp; marketing specs</p>
            </div>

            {/* Interactive Search */}
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/[0.02] hover:bg-white/[0.04] focus:bg-black focus:border-primary/50 text-sm border border-white/5 rounded-xl transition-all outline-none text-white/80 placeholder:text-white/30"
              />
            </div>

            {/* Navigation Lists */}
            <nav className="space-y-6">
              {filteredNav.map((group, idx) => (
                <div key={idx} className="space-y-2">
                  <h4 className="text-[10px] font-bold tracking-widest text-white/30 uppercase pl-3">
                    {group.group}
                  </h4>
                  <ul className="space-y-1">
                    {group.items.map((item, itemIdx) => {
                      const isActive = pathname === item.href
                      const Icon = item.icon
                      return (
                        <li key={itemIdx}>
                          <Link
                            href={item.href}
                            className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all group ${
                              isActive 
                                ? 'bg-primary/10 border border-primary/20 text-white shadow-[0_0_20px_rgba(0,102,255,0.1)]' 
                                : 'border border-transparent text-white/50 hover:text-white hover:bg-white/[0.02]'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <Icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-white/40 group-hover:text-white/70'}`} />
                              <span>{item.label}</span>
                            </div>
                            {isActive && <ChevronRight className="w-3.5 h-3.5 text-primary" />}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
              {filteredNav.length === 0 && (
                <p className="text-sm text-white/30 pl-3 italic">No matching documentation</p>
              )}
            </nav>

          </div>
        </aside>

        {/* Mobile Slide-out Sidebar Drawer */}
        <AnimatePresence>
          {mobileSidebarOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden w-full bg-white/[0.01] backdrop-blur-xl border border-white/5 rounded-2xl p-5 space-y-6"
            >
              {/* Mobile Search */}
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/[0.02] text-sm border border-white/5 rounded-xl outline-none text-white/80 placeholder:text-white/30"
                />
              </div>

              {/* Mobile Navigation */}
              <nav className="space-y-6">
                {filteredNav.map((group, idx) => (
                  <div key={idx} className="space-y-2">
                    <h4 className="text-[10px] font-bold tracking-widest text-white/30 uppercase pl-3">
                      {group.group}
                    </h4>
                    <ul className="space-y-1">
                      {group.items.map((item, itemIdx) => {
                        const isActive = pathname === item.href
                        const Icon = item.icon
                        return (
                          <li key={itemIdx}>
                            <Link
                              href={item.href}
                              onClick={() => setMobileSidebarOpen(false)}
                              className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                isActive 
                                  ? 'bg-primary/10 border border-primary/20 text-white' 
                                  : 'border border-transparent text-white/50 hover:text-white'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <Icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-white/40'}`} />
                                <span>{item.label}</span>
                              </div>
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content Pane */}
        <main className="flex-1 bg-white/[0.01] border border-white/5 rounded-3xl p-6 md:p-10 min-w-0">
          {children}
        </main>

      </div>

      {/* Global Footer */}
      <MarketingFooter />
    </div>
  )
}
